import EditIcon from '@assets/icons/edit.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import TrashIcon from '@assets/icons/trash.svg';
import {
  Animated,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';

enum ActionKeys {
  DELETE = 'delete',
  EDIT = 'edit',
}

export type ActionViewActionHandlers = Partial<{
  [key in ActionKeys]: () => void;
}>;

class ActionsClass extends React.Component<{
  actions: ActionViewActionHandlers;
  conatinerStyle: StyleProp<ViewStyle>;
  iconColor: string;
  iconStyles: { [key in ActionKeys]: StyleProp<ViewStyle> };
  textStyles: StyleProp<TextStyle>;
  touchableOpacity: StyleProp<ViewStyle>;
}> {
  render() {
    const actions = Object.keys(this.props.actions);
    return (
      <View style={this.props.conatinerStyle}>
        {actions.includes(ActionKeys.DELETE) ? (
          <View style={this.props.iconStyles.delete}>
            <TouchableOpacity
              onPress={() => this.props.actions.delete?.()}
              style={this.props.touchableOpacity}
            >
              <TrashIcon fill={this.props.iconColor} />
              <Text style={this.props.textStyles}>{i18n.t('remove')}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        {actions.includes(ActionKeys.EDIT) ? (
          <View style={this.props.iconStyles.edit}>
            <TouchableOpacity
              onPress={() => {
                this.props.actions.edit?.();
              }}
              style={this.props.touchableOpacity}
            >
              <EditIcon fill={this.props.iconColor} />
              <Text style={this.props.textStyles}>{i18n.t('edit')}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  }
}

const AnimatedActions = Animated.createAnimatedComponent(ActionsClass);
export const ACTIONS_CONTAINER_WIDTH = 160;

interface ActionViewProps {
  actions: ActionViewActionHandlers;
  drag: Animated.Value | Animated.AnimatedInterpolation<number>;
  progress: Animated.Value | Animated.AnimatedInterpolation<number>;
}

export const ActionView: React.FC<ActionViewProps> = ({
  actions,
  drag,
  progress,
}) => {
  const [styles, theme] = useStyles();

  const backgroundTransition = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [ACTIONS_CONTAINER_WIDTH, 0],
  });
  const buttonTransition = progress.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [0, 0, ACTIONS_CONTAINER_WIDTH],
  });
  const scale = drag.interpolate({
    extrapolate: 'clamp',
    inputRange: [-1 * ACTIONS_CONTAINER_WIDTH, 0],
    outputRange: [1, 0],
  });

  return (
    <Animated.View
      style={[
        styles.actionsContainer,
        {
          transform: [{ translateX: backgroundTransition }],
        },
      ]}
      testID="action-row"
    >
      <AnimatedActions
        actions={actions}
        conatinerStyle={styles.containerStyle}
        iconColor={theme.colors.accentText}
        iconStyles={{ delete: styles.trashIcon, edit: styles.editIcon }}
        // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/43737
        style={{
          transform: [{ scale, translateX: buttonTransition }],
        }}
        textStyles={styles.textStyles}
        touchableOpacity={styles.touchableOpacity}
      />
    </Animated.View>
  );
};
const iconStyles: StyleProp<ViewStyle> = {
  alignItems: 'center',
  justifyContent: 'center',
  padding: 10,
  width: ACTIONS_CONTAINER_WIDTH / 2, // number of actions to divide total container width by
};

const useStyles = createStyles((theme) => ({
  actionsContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    maxWidth: ACTIONS_CONTAINER_WIDTH,
    position: 'relative',
  },
  containerStyle: {
    flexDirection: 'row',
    height: '100%',
  },
  editIcon: {
    ...iconStyles,
    backgroundColor: theme.colors.blue,
  },
  textStyles: {
    color: theme.colors.accentText,
    fontSize: theme.fontSizes.small,
    paddingTop: 5,
  },
  touchableOpacity: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  trashIcon: {
    ...iconStyles,
    backgroundColor: theme.colors.negative,
  },
}));
